import { GlobalSettingModel, useORPCQuery } from "./oRPC";
import React, { useCallback, useEffect, useState } from "react";
import { applySnapshotToStore, useGlobalSetting } from "Store/GlobalSettingStore";

import { AadAuthRoute } from "./Auth/AadAuthRoute";
import { AuthStatusPage } from "./Auth/AuthStatusPage";
import { LoadingPage } from "Components/Pages/LoadingPage";
import { RenderingStatus } from "Enumerations";
import { observer } from "mobx-react";
import { onSnapshot } from "mobx-state-tree";
import { runInAction } from "mobx";
import { useAfterAuth } from "./Auth/useAfterAuth";
import { useDebouncedCallback } from "Shared/Hooks";
import { useMsal } from "@azure/msal-react";
import { useRootStore } from "Store/RootStore";
import { useMutation, useQuery } from "@tanstack/react-query";

let dirty = false;

const AuthenticatedRouteBase = (props: { children: React.ReactElement }) => {
    const { loginStatus, temporaryLogin } = useRootStore("AuthStore");
    const myStore = useRootStore("MyStore");
    const { coreApi } = useORPCQuery();
    const globalSettingStore = useRootStore("GlobalSetting");
    const settingStorageKey = useRootStore("SettingStorageKey");
    const { mutate } = useMutation(coreApi.postGlobalSetting.mutationOptions());

    const { setLastUserName, setTenantId } = useGlobalSetting("auth");
    const { accounts } = useMsal();
    useAfterAuth();
    const saveUpdatedSettings = useCallback(
        (model: GlobalSettingModel) => {
            mutate(model, { onSuccess: () => (dirty = false) });
        },
        [mutate]
    );

    const saveGlobalSetting = useDebouncedCallback(saveUpdatedSettings, 5000);

    const { data: settings, status } = useQuery(
        coreApi.getGlobalSetting.queryOptions({
            enabled: loginStatus === RenderingStatus.Ready && !settingStorageKey.startsWith("story"),
            staleTime: Infinity
        })
    );

    const [settingLoadingStatus, setSettingLoadingStatus] = useState(RenderingStatus.Waiting);

    useEffect(() => {
        if (status === "success") {
            runInAction(() => {
                const settingValue = settings.find((setting) => setting.SettingName === settingStorageKey)?.Value;
                applySnapshotToStore(globalSettingStore, settingValue);
                onSnapshot(globalSettingStore, (snapshot) => {
                    dirty = true;
                    const model: GlobalSettingModel = {
                        SettingName: settingStorageKey,
                        Value: JSON.stringify(snapshot)
                    };
                    saveGlobalSetting(model);
                });
                setLastUserName(myStore.profile.Upn);
                setTenantId(accounts.find((account) => account.username === myStore.profile.Upn)?.tenantId);
                setSettingLoadingStatus(RenderingStatus.Ready);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    useEffect(() => {
        const onBeforeUnload = (ev: BeforeUnloadEvent) => {
            if (dirty) {
                ev.preventDefault();
            }
        };

        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, []);

    if (loginStatus === RenderingStatus.Ready && (status === "pending" || (settingLoadingStatus === RenderingStatus.Waiting && status !== "error"))) {
        return <LoadingPage label="Please wait while we are loading your settings" />;
    }

    if (temporaryLogin) {
        return <AuthStatusPage>{props.children}</AuthStatusPage>;
    }

    return (
        <AadAuthRoute>
            <AuthStatusPage>{props.children}</AuthStatusPage>
        </AadAuthRoute>
    );
};

export const AuthenticatedRoute = observer(AuthenticatedRouteBase);
